import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { resources as rawResources } from '@/locales'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from '@/env'

// src/locales/{es,en,...}.ts keep each locale's keys flat ({ app, nav, ... }).
// i18next expects a namespace wrapper matching `defaultNS` ('translation'), so
// wrap each locale here at init time. Without this, every t('landing.heroTitle')
// resolves to resources.es.translation.* = undefined and i18next returns the
// key string literally — which is exactly the bug we hit.
const resources = Object.fromEntries(
  Object.entries(rawResources).map(([lng, val]) => [lng, { translation: val }]),
)

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    // Only accept our 9 supported locales; anything else falls back to es.
    supportedLngs: [...SUPPORTED_LOCALES],
    // Any missed key falls back to English (the default locale). Every bundle
    // is complete, so this only guards against a future gap.
    fallbackLng: { default: [DEFAULT_LOCALE] },
    nonExplicitSupportedLngs: true, // 'es-AR' → 'es'
    load: 'languageOnly',
    ns: ['translation'],
    defaultNS: 'translation',
    interpolation: { escapeValue: false },
    detection: {
      // Order: explicit URL locale is handled by the router; here we also honor
      // localStorage + browser so first visit lands on a sensible locale.
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'bitutravel.locale',
      caches: ['localStorage'],
    },
  })

/**
 * Best locale for a first-time visitor. i18next-browser-languagedetector
 * already ran at init (order: localStorage → navigator → <html lang>), so
 * `resolvedLanguage` honors a returning user's cached choice, then the device
 * language. We validate it against our supported set and fall back to the
 * device language's base code, then to English — never to a hardcoded locale.
 */
export function detectedLocale(): Locale {
  const picked = i18n.resolvedLanguage ?? i18n.language
  if (picked && (SUPPORTED_LOCALES as readonly string[]).includes(picked)) return picked as Locale
  const nav = typeof navigator !== 'undefined' ? navigator.language?.slice(0, 2).toLowerCase() : undefined
  if (nav && (SUPPORTED_LOCALES as readonly string[]).includes(nav)) return nav as Locale
  return DEFAULT_LOCALE
}

/** Resolve a user-generated JSONB locale field (e.g. property.title) with fallback. */
export function localized(
  value: Record<string, string> | null | undefined,
  locale: Locale,
): string {
  if (!value) return ''
  return value[locale] || value[DEFAULT_LOCALE] || Object.values(value)[0] || ''
}

/** Change locale app-wide and persist. Router mirrors this in the URL prefix. */
export function setLocale(locale: Locale) {
  i18n.changeLanguage(locale)
}

export default i18n